import jsPDF from 'jspdf';

const pdfTranslations = {
  en: {
    title: 'CLAIM / INCIDENT REPORT',
    subtitle: 'Université Senghor — Résidences',
    reportDate: 'Report Date',
    section1: '1. GUEST INFORMATION',
    section2: '2. STAY INFORMATION',
    section3: '3. CHECK-OUT VERIFICATION',
    section4: '4. CLAIM INFORMATION',
    section5: '5. ATTACHMENTS',
    fullName: 'Full Name:',
    department: 'Department:',
    financialStatus: 'Financial Status:',
    roomNumber: 'Room Number:',
    building: 'Building:',
    checkIn: 'Check-in Date:',
    checkOut: 'Check-out Date:',
    keyReturned: 'Key / Access Card Returned:',
    roomCheck: 'Room Check Completed:',
    issueDescription: 'Issue Description:',
    dateOfReport: 'Date of Report:',
    reportedBy: 'Reported By (Admin):',
    photosAttached: (n) => `${n} photo(s) attached to this report.`,
    photo: 'Photo',
    yes: 'Yes ✓',
    no: 'No ✗',
    footer: 'Université Senghor — Résidences | Confidential Document',
    pageOf: (i, total) => `Page ${i} of ${total}`,
    na: '—',
  },
  fr: {
    title: 'RAPPORT DE RÉCLAMATION / INCIDENT',
    subtitle: 'Université Senghor — Résidences',
    reportDate: 'Date du rapport',
    section1: '1. INFORMATIONS SUR L\'INVITÉ',
    section2: '2. INFORMATIONS SUR LE SÉJOUR',
    section3: '3. VÉRIFICATION DE DÉPART',
    section4: '4. INFORMATIONS SUR LA RÉCLAMATION',
    section5: '5. PIÈCES JOINTES',
    fullName: 'Nom complet :',
    department: 'Département :',
    financialStatus: 'Statut financier :',
    roomNumber: 'Numéro de chambre :',
    building: 'Bâtiment :',
    checkIn: 'Date d\'arrivée :',
    checkOut: 'Date de départ :',
    keyReturned: 'Clé / Carte d\'accès rendue :',
    roomCheck: 'Vérification de la chambre :',
    issueDescription: 'Description du problème :',
    dateOfReport: 'Date du rapport :',
    reportedBy: 'Signalé par (Admin) :',
    photosAttached: (n) => `${n} photo(s) jointe(s) à ce rapport.`,
    photo: 'Photo',
    yes: 'Oui ✓',
    no: 'Non ✗',
    footer: 'Université Senghor — Résidences | Document confidentiel',
    pageOf: (i, total) => `Page ${i} sur ${total}`,
    na: '—',
  },
};

/**
 * Generate a claim report PDF
 * @param {Object} claim - The claim data
 * @param {string} language - 'en' or 'fr'
 * @returns {jsPDF} - The PDF document
 */
export async function generateClaimReportPDF(claim, language = 'en') {
  const lx = pdfTranslations?.[language] || pdfTranslations?.en;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // ── Header with logo ──────────────────────────────────────────────────────
  try {
    const logoUrl = '/assets/images/logo_USenghor_vertical_blanc_1000_min-1774525392061.png';
    const response = await fetch(logoUrl);
    const blob = await response?.blob();
    const reader = new FileReader();
    const logoData = await new Promise((resolve) => {
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(blob);
    });

    // Load image to get natural dimensions for proportional scaling
    const img = new Image();
    const naturalDims = await new Promise((resolve) => {
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ w: 1, h: 2 });
      img.src = logoData;
    });

    const targetH = 30;
    const aspectRatio = naturalDims?.w / naturalDims?.h;
    const targetW = targetH * aspectRatio;

    doc?.setFillColor(15, 23, 42);
    doc?.rect(margin, y, targetW + 4, 30, 'F');
    doc?.addImage(logoData, 'PNG', margin + 2, y, targetW, targetH);

    doc?.setFillColor(15, 23, 42);
    doc?.rect(margin + targetW + 4, y, contentWidth - targetW - 4, 30, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(16);
    doc?.setFont('helvetica', 'bold');
    doc?.text(lx?.title, margin + targetW + 7, y + 10);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.text(lx?.subtitle, margin + targetW + 7, y + 17);
    doc?.text(`${lx?.reportDate}: ${claim?.reportDate || new Date()?.toISOString()?.split('T')?.[0]}`, margin + targetW + 7, y + 24);
  } catch (e) {
    doc?.setFillColor(15, 23, 42);
    doc?.rect(margin, y, contentWidth, 30, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(16);
    doc?.setFont('helvetica', 'bold');
    doc?.text(lx?.title, margin + 5, y + 10);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.text(lx?.subtitle, margin + 5, y + 17);
    doc?.text(`${lx?.reportDate}: ${claim?.reportDate || new Date()?.toISOString()?.split('T')?.[0]}`, margin + 5, y + 24);
  }

  y += 38;

  // ── Section helper ────────────────────────────────────────────────────────
  const drawSection = (title, color = [241, 245, 249]) => {
    doc?.setFillColor(...color);
    doc?.rect(margin, y, contentWidth, 8, 'F');
    doc?.setTextColor(15, 23, 42);
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.text(title, margin + 3, y + 5.5);
    y += 11;
  };

  const drawRow = (label, value, indent = 0) => {
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(100, 116, 139);
    doc?.text(label, margin + indent, y);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(15, 23, 42);
    const valStr = value != null ? String(value) : lx?.na;
    const lines = doc?.splitTextToSize(valStr, contentWidth - 60);
    doc?.text(lines, margin + 60, y);
    y += lines?.length * 5 + 2;
  };

  // ── Guest Information ─────────────────────────────────────────────────────
  drawSection(lx?.section1);
  drawRow(lx?.fullName, claim?.guestName || lx?.na);
  drawRow(lx?.department, claim?.department || lx?.na);
  drawRow(lx?.financialStatus, claim?.financialStatus || lx?.na);
  y += 3;

  // ── Stay Information ──────────────────────────────────────────────────────
  drawSection(lx?.section2);
  drawRow(lx?.roomNumber, claim?.room || lx?.na);
  drawRow(lx?.building, claim?.building || lx?.na);
  drawRow(lx?.checkIn, claim?.checkIn || lx?.na);
  drawRow(lx?.checkOut, claim?.checkOut || lx?.na);
  y += 3;

  // ── Check-out Verification ────────────────────────────────────────────────
  drawSection(lx?.section3);
  drawRow(lx?.keyReturned, claim?.keyReturned ? lx?.yes : lx?.no);
  drawRow(lx?.roomCheck, claim?.roomCheckCompleted ? lx?.yes : lx?.no);
  y += 3;

  // ── Claim Information ─────────────────────────────────────────────────────
  drawSection(lx?.section4, [254, 242, 242]);
  drawRow(lx?.issueDescription, claim?.description || lx?.na);
  drawRow(lx?.dateOfReport, claim?.reportDate || lx?.na);
  drawRow(lx?.reportedBy, claim?.reportedBy || lx?.na);
  y += 3;

  // ── Attachments ───────────────────────────────────────────────────────────
  if (claim?.photos && claim?.photos?.length > 0) {
    drawSection(lx?.section5);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(100, 116, 139);
    doc?.text(lx?.photosAttached(claim?.photos?.length), margin + 3, y);
    y += 7;

    let photoX = margin;
    let photoY = y;
    const photoW = 55;
    const photoH = 40;
    const photosPerRow = 3;

    for (let i = 0; i < claim?.photos?.length; i++) {
      if (i > 0 && i % photosPerRow === 0) {
        photoX = margin;
        photoY += photoH + 5;
      }
      if (photoY + photoH > doc?.internal?.pageSize?.getHeight() - margin) {
        doc?.addPage();
        photoY = margin;
        photoX = margin;
      }
      try {
        doc?.addImage(claim?.photos?.[i], 'JPEG', photoX, photoY, photoW, photoH);
        doc?.setDrawColor(200, 200, 200);
        doc?.rect(photoX, photoY, photoW, photoH);
        doc?.setFontSize(7);
        doc?.setTextColor(150, 150, 150);
        doc?.text(`${lx?.photo} ${i + 1}`, photoX + 2, photoY + photoH - 2);
      } catch (e) {
        doc?.setFillColor(240, 240, 240);
        doc?.rect(photoX, photoY, photoW, photoH, 'F');
        doc?.setFontSize(8);
        doc?.setTextColor(150, 150, 150);
        doc?.text(`${lx?.photo} ${i + 1}`, photoX + photoW / 2 - 8, photoY + photoH / 2);
      }
      photoX += photoW + 5;
    }
    y = photoY + photoH + 8;
  }

  // ── Footer ────────────────────────────────────────────────────────────────
  const pageCount = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc?.setPage(i);
    doc?.setDrawColor(226, 232, 240);
    doc?.line(margin, doc?.internal?.pageSize?.getHeight() - 15, pageWidth - margin, doc?.internal?.pageSize?.getHeight() - 15);
    doc?.setFontSize(8);
    doc?.setTextColor(148, 163, 184);
    doc?.setFont('helvetica', 'normal');
    doc?.text(lx?.footer, margin, doc?.internal?.pageSize?.getHeight() - 9);
    doc?.text(lx?.pageOf(i, pageCount), pageWidth - margin - 20, doc?.internal?.pageSize?.getHeight() - 9);
  }

  return doc;
}

export function downloadClaimPDF(claim, language = 'en') {
  return generateClaimReportPDF(claim, language)?.then(doc => {
    const filename = `claim-report-${claim?.room || 'room'}-${claim?.reportDate || 'date'}.pdf`;
    doc?.save(filename);
  });
}
